import { defineComponent, onMounted, ref, reactive, watch } from '@vue/composition-api';
import MessageConst from '@/shared/message-const';
import CommonFunction from '@/shared/common-function';

/**
 * フィルタコンポーネント
 */

/**
 * フィルタの種類
 * KeyWordPerfectMatch: 完全一致（テキストボックス）
 * KeyWordPartialMatch: 部分一致（テキストボックス）
 * DatePeriod: 範囲指定（カレンダー）（開始～終了）
 * NumRange: 範囲指定（テキストボックス）（開始～終了）
 * PullDown: 択一選択（プルダウン）
 * PullDownMulti: 複数選択（プルダウン）
 * CheckBox: 複数選択(OR)（チェックボックス）
 */
type FilterTypes = 'KeyWordPerfectMatch' | 'KeyWordPartialMatch' | 'DatePeriod' | 'NumRange' | 'PullDown' | 'PullDownMulti' | 'CheckBox' | 'IssueNumber' | 'PublicationDate' | 'FreeWord'

/**
 * ダイアログへ表示する選択項目
 */
interface Item {
    text: string,                       // 表示内容
    value: string,                      // コード
}

/**
 * チェックボックスへ表示する選択項目
 */
interface CheckItem {
    text: string,                       // 表示内容
    value: string,                      // コード
    isChecked?: boolean,                // チェック有無
}

/**
 * 画面から入力したフィルタ条件を保持
 */
interface FilterCond {
    keyword: string,                    // キーワード（文字列）
    startDate: string,                  // 開始日時
    endDate: string,                    // 終了日時
    formattedStartDate: string,         // 表示用開始日時
    formattedEndDate: string,           // 表示用終了日時
    oneItem: Item,                      // プルダウン（択一選択）
    multiItem: Item[],                  // プルダウン（複数選択）
    minNumber: string,                  // 最小値
    maxNumber: string,                  // 最大値
    chkItems: CheckItem[],              // チェックボックス・ラジオボタン
}

/**
 * フィルタ条件（画面へ返すもの）
 */
interface FilterDef {
    id: string,                         // フィルタ条件を一意に識別するID( "organizationId" 等)
    title: string,                      // 画面に表示するタイトル( "組織ID" 等 )
    types: FilterTypes,                 // フィルタの種類
    isEnabled: boolean,                 // フィルタが有効であればtrue
    condition: FilterCond,              // フィルタ条件
    showDialog: boolean,                // フィルタ条件を入力するダイアログの表示切替
}

/**
 * フィルタ条件（内部処理用）
 */
interface FilterDefTmp {
    id: string,                         // フィルタ条件を一意に識別するID( "organizationId" 等)
    chkItemsSelected: string[],         // チェックボックスのチェック内容
    radioButtonSelected: string         // ラジオボタンのチェック内容
    formattedStartDate: string,         // フィルタを追加ボタンの右側へ表示するフォーマット済日付
    formattedEndDate: string,           // フィルタを追加ボタンの右側へ表示するフォーマット済日付
    isVisibleStartDateMenu: boolean,    // 開始日付のv-menu表示制御
    isVisibleEndDateMenu: boolean,      // 終了日付のv-menu表示制御
}

/**
 * １画面分のフィルタ条件
 */
export interface TableFilters {
    [key: string]: FilterDef            // フィルタ条件を保持するフィルタ
}

/**
 * １画面分のフィルタ条件（内部処理用）
 */
export interface TableFilterTmp {
    [key: string]: FilterDefTmp         // 内部処理用
}

/**
 * 初期化用フィルタ条件
 */
interface FilterDefsInit {
    title: string,                      // 画面に表示するタイトル( "組織ID" 等 )
    types: FilterTypes,                 // フィルタの種類
    validator?: any,                    // バリデータ
    startValidator?: any,               // バリデータ（開始項目）
    endValidator?: any,                 // バリデータ（終了項目）
    items?: Item[],                     // ダイアログへ表示するプルダウン項目
    chkItems?: CheckItem[],             // ダイアログへ表示するチェックボックス・ラジオボタン項目
}

/**
 * 親コンポーネントが設定する初期化用フィルタ条件
 */
export interface TableFilterDefs {
    [key: string]: FilterDefsInit       // フィルタ条件を保持するフィルタ
}

/**
 * リアクティブな変数の型定義
 */
export type reactiveData = {
    tableFilter: TableFilters,
    tableFilterTmp: TableFilterTmp,
    tableFilterDef: TableFilterDefs,
    visibleFilterMenu: boolean,
    buttonCoordinate: { x: Element | number | null, y: Element | number | null },
    searchPullDownItem: string,
    searchPullDownMulti: string,
    isDarkTheme: boolean
};

/**
 * 引数
 */
interface Props {
    filterDefs: TableFilterDefs
}

/**
 * フィルタコンポーネント
 */
export default defineComponent({
    props: {
        filterDefs: Object
    },
    setup(props: Props, context) {

        // リアクティブな変数の宣言
        const state: reactiveData = reactive<reactiveData>({
            tableFilter: {},
            tableFilterTmp: {},
            tableFilterDef: {},
            visibleFilterMenu: false,
            buttonCoordinate: { x: null, y: null },
            searchPullDownItem: '',                          // 選択プルダウンの検索条件入力値
            searchPullDownMulti: '',                         // 複数選択プルダウンの検索条件入力値
            isDarkTheme: context.root.$store.state.commonLayout.isDarkTheme
        });

        // storeのダークテーマ使用フラグを監視し、ダークテーマ使用フラグの変更を行う
        watch(context.root.$store.state.commonLayout, () => {
            state.isDarkTheme = context.root.$store.state.commonLayout.isDarkTheme;
        })
        // バリデーション用
        const form = ref<any>(null);

        /**
         * 現在表示しているフィルタダイアログのid
         */
        let displayDialogId = ref<any>(null);

        onMounted(() => {
            // 親コンポーネントからの引数をコピーする
            const filters: TableFilters = Object.fromEntries(
                Object.keys(props.filterDefs).map(key => [key, {
                    id: key,
                    title: props.filterDefs[key].title,
                    types: props.filterDefs[key].types,
                    isEnabled: false,
                    showDialog: false,
                    words: '',
                    condition: {
                        keyword: '',
                        startDate: '',
                        endDate: '',
                        formattedStartDate: '',
                        formattedEndDate: '',
                        oneItem: { text: '', value: '' },
                        multiItem: [],
                        minNumber: '',
                        maxNumber: '',
                        chkItems: props.filterDefs[key].chkItems,
                    }
                }])
            ) as TableFilters;

            // 内部処理用の変数を初期化
            const filterTmp: TableFilterTmp = Object.fromEntries(
                Object.keys(props.filterDefs).map(key => [key, {
                    id: key,
                    chkItemsSelected: [],
                    radioButtonSelected: '',
                    formattedStartDate: '',
                    formattedEndDate: '',
                    isVisibleStartDateMenu: false,
                    isVisibleEndDateMenu: false,
                }])
            ) as TableFilterTmp;

            state.tableFilterDef = props.filterDefs;
            state.tableFilter = filters;
            state.tableFilterTmp = filterTmp;
        })

        /**
         * フィルタ条件の有効確認
         * 
         * @returns １つでもフィルタが有効であればtrue
         */
        function isFilterEnabled(): boolean {
            if (Object.keys(state.tableFilter).length != 0) {
                return Object.values(state.tableFilter)
                    .map(val => val.isEnabled as boolean)
                    .reduce((x, y) => (x || y));
            } else {
                // フィルタ条件が１つもない場合
                return false;
            }
        }

        /**
         * フィルタメニューを閉じる
         */
        function closeFilterMenu() {
            state.visibleFilterMenu = false;
        }

        /**
         * 指定したフィルタをクリア
         * 
         * @param id クリアするフィルタを識別するID
         */
        function clear(id: string) {
            clearData(id);
            notify();
        }

        /**
         * 全てのフィルタをクリア
         */
        function allClear() {
            Object.keys(state.tableFilter).forEach(key => clearData(key));
            notify();
        }

        /**
         * フィルタを管理するデータを初期化
         * 
         * @param id クリアするフィルタを識別するID
         */
        function clearData(id: string) {
            state.tableFilter[id].isEnabled = false;
            state.tableFilter[id].showDialog = false;
            state.tableFilter[id].condition.keyword = '';
            state.tableFilter[id].condition.startDate = '';
            state.tableFilter[id].condition.endDate = '';
            state.tableFilter[id].condition.minNumber = '';
            state.tableFilter[id].condition.maxNumber = '';
            state.tableFilter[id].condition.oneItem = { text: '', value: '' };
            state.tableFilter[id].condition.multiItem = [];
            // チェックボックスは定義をコピー
            if ('chkItems' in state.tableFilterDef[id]) {
                state.tableFilter[id].condition.chkItems = [...state.tableFilterDef[id].chkItems as CheckItem[]];
                state.tableFilter[id].condition.chkItems.forEach(e => e.isChecked = false);
            }
            state.tableFilterTmp[id].chkItemsSelected = [];
            state.tableFilterTmp[id].formattedStartDate = '';
            state.tableFilterTmp[id].formattedEndDate = '';
        }

        /**
         * 有効なフィルタの一覧取得
         * vue内で有効なフィルタを列挙する際に用いる
         * 
         * @returns 有効なフィルタの一覧
         */
        function getFiltered() {
            return Object.values(state.tableFilter).filter(v => v.isEnabled);
        }

        /**
         * 有効でないフィルタの一覧取得
         * vue内でまだ有効になっていないフィルタをリスト表示する際に用いる
         * 
         * @returns 有効でないフィルタの一覧
         */
        function getNonFiltered() {
            return Object.values(state.tableFilter).filter(v => !v.isEnabled);
        }

        /**
         * 有効なフィルタの条件取得
         * vue内で有効なフィルタを列挙する際に用いる
         * 
         * @param id 表示したいフィルタを識別するID
         * @returns 整形された条件文字列
         */
        function getCondition(id: string): string {
            // 型により返す文字列を整形して返す
            // KeyWordPerfectMatch: 設定した文字列をそのまま返す
            // KeyWordPartialMatch: 設定した文字列をそのまま返す
            // FreeWord: 設定した文字列をそのまま返す
            // DatePeriod: YYYY/MM/DD ～ YYYY/MM/DD
            // PublicationDate: YYYY/MM/DD ～ YYYY/MM/DD
            // NumRange: X ～ X
            // PullDown/PullDownMulti/CheckBox: "条件1", "条件2", "条件3", ...
            if (state.tableFilterDef[id].types === 'KeyWordPerfectMatch' ||
                state.tableFilterDef[id].types === 'KeyWordPartialMatch' ||
                state.tableFilterDef[id].types === 'FreeWord') {
                // 設定した文字列をそのまま返す
                return state.tableFilter[id].condition.keyword;
            } else if (state.tableFilterDef[id].types === 'DatePeriod' || state.tableFilterDef[id].types === 'PublicationDate') {
                // YYYY/MM/DD ～ YYYY/MM/DDの形式で返す
                return (state.tableFilterTmp[id].formattedStartDate || '')
                    + ' ～ '
                    + (state.tableFilterTmp[id].formattedEndDate || '');
            } else if (state.tableFilterDef[id].types === 'NumRange') {
                // 最小値 ～ 最大値の形式で返す
                return state.tableFilter[id].condition.minNumber +
                    ' ～ ' +
                    state.tableFilter[id].condition.maxNumber;
            } else if (state.tableFilterDef[id].types === 'PullDown') {
                // text値を返す
                return state.tableFilter[id].condition.oneItem.text;
            } else if (state.tableFilterDef[id].types === 'PullDownMulti') {
                // text
                return Object.values(state.tableFilter[id].condition.multiItem).map(v => v.text).join(',');
            } else if (state.tableFilterDef[id].types === 'CheckBox') {
                // textをカンマ区切りで返す
                return state.tableFilterTmp[id].chkItemsSelected.map(x => {
                    const kv = state.tableFilterDef[id].chkItems;
                    if (kv) {
                        const oneKv = kv.find(kv => kv.value === x);
                        if (oneKv) {
                            return oneKv.text;
                        } else {
                            return '';
                        }
                    }
                }).join('、');
            } else if (state.tableFilterDef[id].types === 'IssueNumber') {
                // 画面で表示されている項目名をそのまま出力
                return state.tableFilter[id].condition.chkItems.map(x => {
                    if (x.isChecked) {
                        if (state.tableFilterTmp[id].radioButtonSelected === '0') {
                            // 発番指定の場合
                            return x.text + '第' + state.tableFilter[id].condition.keyword + '号';
                        } else {
                            // 事務連絡の場合
                            return x.text;
                        }
                    }
                }).join('');
            }

            return "not implemented";
        }

        /**
         * ダイアログで適用ボタン押下
         * 
         * @param id 適用したフィルタを識別するID
         */
        function setFilter(id: string) {
            // バリデーション実行
            if (!form.value.validate()) {
                return;
            }

            // フィルタ条件を有効にする
            state.tableFilter[id].isEnabled = true;

            // 日付範囲のカレンダーを閉じる
            isVisibleCalendar(id);

            // ダイアログを閉じる
            state.tableFilter[id].showDialog = false;

            // メニューを閉じる
            state.visibleFilterMenu = false;

            // チェックボックスのみ、値を整理
            getCheckBoxValues(id);

            // ラジオボタンの選択状態を反映
            getRadioButtonValues(id);

            // 親コンポーネントへ通知する
            notify();

            // 表示中のダイアログのidをリセット
            displayDialogId = null;

            // プルダウン検索文字列の初期化
            state.searchPullDownItem = '';
        }

        /**
         * vuexで保持していたデータを再表示
         * 
         * @param id 適用されていたすべての識別用フィルタID
         * @param condition 検索条件の値
         */
        function setAllFilters(id: string[], condition: FilterCond[]) {
            for (let i = 0; id.length > i; i++) {
                // フィルタ条件を有効にする
                state.tableFilter[id[i]].isEnabled = true;

                switch (state.tableFilter[id[i]].types) {
                    case 'DatePeriod':
                    case 'PublicationDate':
                        // 日付範囲の検索条件の値を入力する
                        state.tableFilterTmp[id[i]].formattedStartDate = condition[i].startDate;
                        state.tableFilterTmp[id[i]].formattedEndDate = condition[i].endDate;
                        state.tableFilter[id[i]].condition.startDate = condition[i].startDate;
                        state.tableFilter[id[i]].condition.endDate = condition[i].endDate;
                        break;

                    case 'NumRange':
                        // 数値範囲の検索条件の値を入力する
                        state.tableFilter[id[i]].condition.minNumber = condition[i].minNumber;
                        state.tableFilter[id[i]].condition.maxNumber = condition[i].maxNumber;
                        break;

                    case 'PullDownMulti':
                        // 複数選択（プルダウン）の検索条件の値を入力する
                        state.tableFilter[id[i]].condition.multiItem = condition[i].multiItem;
                        break;

                    case 'CheckBox':
                        // 複数選択（チェックボックス）の検索条件の値を入力する
                        for (let j = 0; condition[i].chkItems.length > j; j++) {
                            if (condition[i].chkItems[j].isChecked === true) {
                                state.tableFilterTmp[id[i]].chkItemsSelected.push(condition[i].chkItems[j].value);
                            }
                        }
                        state.tableFilter[id[i]].condition.chkItems = condition[i].chkItems;
                        break;

                    default:
                        // その他の検索条件の値を入力する
                        state.tableFilter[id[i]].condition = condition[i];
                        break;
                }
            }

            // 親コンポーネントへ通知する
            notify();
        }

        /**
         * チェック済のチェックボックスを取得し、
         * 定義と比較して、isCheckの更新を行う。
         * 
         * @param id 適用したフィルタを識別するID
         */
        function getCheckBoxValues(id: string): void {
            if (state.tableFilterDef[id].types === 'CheckBox') {
                // 初めてチェックする場合の初期化
                // 親画面でDBから取得したチェックボックス項目を後から設定するため
                if (state.tableFilter[id].condition.chkItems === undefined) {
                    state.tableFilter[id].condition.chkItems = state.tableFilterDef[id].chkItems as CheckItem[];
                    state.tableFilter[id].condition.chkItems.forEach(x => x.isChecked = false);
                }

                // チェックされた項目からフラグのON/OFFを設定する
                state.tableFilter[id].condition.chkItems.forEach(x => {
                    const find = state.tableFilterTmp[id].chkItemsSelected.find(y => y === x.value)
                    if (find) {
                        x.isChecked = true;
                    } else {
                        x.isChecked = false;
                    }
                });
            }
        }

        /**
         * 選択済のラジオボタンを取得し、
         * 定義と比較して、isCheckedの更新を行う。
         * 
         * @param id 適用したフィルタを識別するID
         */
        function getRadioButtonValues(id: string): void {
            if (state.tableFilterDef[id].types === 'IssueNumber') {
                // 初めて選択する場合の初期化
                // 親画面でDBから取得したラジオボタン項目を後から設定するため
                if (state.tableFilter[id].condition.chkItems === undefined) {
                    state.tableFilter[id].condition.chkItems = state.tableFilterDef[id].chkItems as CheckItem[];
                    state.tableFilter[id].condition.chkItems.forEach(x => x.isChecked = false);
                }

                // ラジオボタン項目のON/OFFを設定する
                state.tableFilter[id].condition.chkItems.forEach(x => {
                    if (state.tableFilterTmp[id].radioButtonSelected === x.value) {
                        x.isChecked = true;
                    } else {
                        x.isChecked = false;
                    }
                });
            }
        }

        /**
         * カレンダー表示のパラメータを変更する
         *
         * @param id 適用したフィルタを識別するID
         */
        function isVisibleCalendar(id: string): void {
            // 日付範囲のフィルタダイアログを閉じた場合
            if (state.tableFilterDef[id].types === 'DatePeriod' || state.tableFilterDef[id].types === 'PublicationDate') {
                // カレンダーメニューを閉じる
                state.tableFilterTmp[id].isVisibleStartDateMenu = false;
                state.tableFilterTmp[id].isVisibleEndDateMenu = false;
            }
        }

        /**
         * 閉じるボタン押下イベント
         * 
         * @param id 適用したフィルタを識別するID
         */
        function closeDialog(id: string) {
            // ダイアログを閉じる
            state.tableFilter[id].showDialog = false;
            isVisibleCalendar(id);
        }

        /**
         * 閉じられたダイアログの入力状態をリセットするイベント
         * （閉じるボタン押下イベントの後にも呼ばれる）
         * 
         */
        function clickDialogOutside() {
            // 表示しているダイアログが閉じられたら入力をリセットし、バリデーションをリセットする
            if (displayDialogId && !state.tableFilter[displayDialogId].showDialog) {
                // バリデーションをリセットする
                form.value.resetValidation();
                // 入力をリセットする（データクリアのみで親コンポーネントへ通知しない）
                clearData(displayDialogId);
                // ダイアログを閉じる
                state.tableFilter[displayDialogId].showDialog = false;
                // 表示中のダイアログのidをリセット
                displayDialogId = null;
            }
        }

        /**
         * 日付を表示用にフォーマット変換する
         * 
         * @param isStart 開始日付:true 終了日付:false
         * @param dateValue YYYY-MM-DD形式文字列
         * @return YYYY/MM/DD形式文字列
         */
        function inputDate(isStart: boolean, id: string, dateValue: string) {
            // 開始日付
            if (isStart) {
                // YYYY-MM-DD ⇒ YYYY/MM/DD
                state.tableFilterTmp[id].formattedStartDate = dateValue.replaceAll('-', '/');
                state.tableFilterTmp[id].isVisibleStartDateMenu = false;
            } else {
                state.tableFilterTmp[id].formattedEndDate = dateValue.replaceAll('-', '/');
                state.tableFilterTmp[id].isVisibleEndDateMenu = false;
            }
        }

        /**
         * テキストボックスに表示されている日付をクリア
         * 
         * @param isStart 開始日付:true 終了日付:false
         * @param id 適用したフィルタを識別するID
         */
        function clearDate(isStart: boolean, id: string) {
            // 開始
            if (isStart) {
                // 表示をクリア(YYYY/MM/DD形式)
                state.tableFilterTmp[id].formattedStartDate = '';
                // 内部保持値をクリア
                state.tableFilter[id].condition.startDate = '';
            } else {
                state.tableFilterTmp[id].formattedEndDate = '';
                state.tableFilter[id].condition.endDate = '';
            }
        }

        /**
         * プルダウンフィルタのclearableボタン押下時処理
         */
        function pulldownClear(id: string) {
            // nullになった場合、データ初期化
            if (state.tableFilter[id].condition.oneItem === null) {
                state.tableFilter[id].condition.oneItem = { text: '', value: '' };
            }
        }

        /**
         * バリデーション（部分一致）
         */
        const defaultKeyWordPartialMatchValidator = [
            // 必須入力
            () => {
                for (const key in state.tableFilter) {
                    if ((state.tableFilterDef[key].types === 'KeyWordPartialMatch' || state.tableFilterDef[key].types === 'FreeWord') && state.tableFilter[key].showDialog) {
                        return !!state.tableFilter[key].condition.keyword || CommonFunction.replaceArray(MessageConst.E_REQUIRED, [state.tableFilterDef[key].title]);
                    }
                }
            }
        ];

        /**
         * バリデーション（日付範囲）
         */
        function defaultDatePeriodValidator(): any {
            // 逆転チェック、両方空はNG
            for (const key in state.tableFilter) {
                if ((state.tableFilterDef[key].types === 'DatePeriod' || state.tableFilterDef[key].types === 'PublicationDate') && state.tableFilter[key].showDialog) {
                    const s = state.tableFilter[key].condition.startDate;
                    const e = state.tableFilter[key].condition.endDate;
                    // 日付の逆転チェック
                    if (s && e && s > e) {
                        return [MessageConst.E_DATE_ORDER];
                    }

                    // 少なくとも１つは入力必須
                    if (!s && !e) {
                        return [MessageConst.E_INPUT_AT_LEAST_ONE];
                    }

                    return true;
                }
            }
        }

        /**
         * バリデーション（数値範囲）
         */
        function defaultNumRangeValidator(): any {
            // 逆転チェック、両方空はNG
            for (const key in state.tableFilter) {
                if (state.tableFilterDef[key].types === 'NumRange' && state.tableFilter[key].showDialog) {
                    const s = state.tableFilter[key].condition.minNumber;
                    const e = state.tableFilter[key].condition.maxNumber;
                    // 数値の逆転チェック
                    if (s && e && Number(s) > Number(e)) {
                        return [MessageConst.E_NUM_ORDER];
                    }

                    // 少なくとも１つは入力必須
                    if (!s && !e) {
                        return [MessageConst.E_INPUT_AT_LEAST_ONE];
                    }

                    return true;
                }
            }
        }

        /**
         * バリデーション（チェックボックス）
         */
        const defaultCheckBoxValidator = [
            // １つ以上チェックが必要
            () => {
                for (const key in state.tableFilter) {
                    if (state.tableFilterDef[key].types === 'CheckBox' && state.tableFilter[key].showDialog) {
                        const count = state.tableFilterTmp[key].chkItemsSelected.length;
                        // 少なくとも１つは入力必須
                        if (count === 0) {
                            return MessageConst.E_CHECK_AT_LEAST_ONE;
                        }

                        return true;
                    }
                }
            }
        ];

        /**
         * バリデーション（プルダウン（択一選択））
         * 
         */
        const defaultOneItemValidator = [
            // 選択が必要
            () => {
                for (const key in state.tableFilter) {
                    if (state.tableFilterDef[key].types === 'PullDown' && state.tableFilter[key].showDialog) {
                        const item = state.tableFilter[key].condition.oneItem;
                        if (item.value) {
                            return true;
                        } else {
                            return MessageConst.E_SELECT_ONE;
                        }
                    }
                }
            }
        ];

        /**
         * バリデーション（プルダウン（複数選択））
         * 
         */
        const defaultMultiItemValidator = [
            // 選択が必要
            () => {
                for (const key in state.tableFilter) {
                    if (state.tableFilterDef[key].types === 'PullDownMulti' && state.tableFilter[key].showDialog) {
                        const count = state.tableFilter[key].condition.multiItem.length;
                        if (count === 0) {
                            return MessageConst.E_SELECT_ONE;
                        }
                        return true;
                    }
                }
            }
        ];

        /**
         * バリデーション（公文書発番のラジオボタン）
         */
        const defaultIssueNumberRadioButtonValidator = [
            // 必須入力
            () => {
                for (const key in state.tableFilter) {
                    if (state.tableFilterDef[key].types === 'IssueNumber' && state.tableFilter[key].showDialog) {
                        if (state.tableFilterTmp[key].radioButtonSelected === '') {
                            return MessageConst.E_SELECT_ONE;
                        }
                    }
                }
            }
        ];

        /**
         * バリデーション（公文書発番のテキスト）
         * 
         */
        const defaultIssueNumberTextValidator = [
            // 必須入力
            () => {
                for (const key in state.tableFilter) {
                    if (state.tableFilterDef[key].types === 'IssueNumber' && state.tableFilter[key].showDialog && state.tableFilterTmp[key].radioButtonSelected === '0') {
                        return !!state.tableFilter[key].condition.keyword || CommonFunction.replaceArray(MessageConst.E_REQUIRED, [state.tableFilterDef[key].title]);
                    }
                }
            }
        ];

        /**
         * 複数選択プルダウンのデータの選択が変更された時の処理
         */
        function oneSetPullData() {
            // 検索項目の値を初期化する
            state.searchPullDownMulti = '';
        }

        /**
         * 発番ラジオボタンの選択イベント
         */
        function divisionChange() {
            for (const key in state.tableFilter) {
                // 事務連絡のラジオボタンを選択した際に、発番の入力欄を空にする
                if (state.tableFilterDef[key].types === 'IssueNumber' && state.tableFilterTmp[key].radioButtonSelected !== '0' && state.tableFilter[key].showDialog) {
                    state.tableFilter[key].condition.keyword = '';
                }
            }
        }

        /**
         * 一定以上の長さのフィルタ表示用文字列を省略する処理
         * 
         * @param value フィルタ入力文字列
         */
        function getOmitString(value: string) {
            const length = 30;
            if (value.length <= length) {
                return value;
            }
            return value.substring(0, length) + "...";
        }

        /**
         * 親コンポーネントへフィルタ条件が更新されたことを通知する
         */
        function notify() {
            // 親コンポーネントのupdateFilterイベント
            context.emit('updateFilter', state.tableFilter);
        }

        /**
         * フィルタ選択時にフィルタメニューを閉じ、ダイアログを開く
         * 
         * @param id 適用したフィルタを識別するID
         */
        function downMenuUpDialog(id: string) {
            state.tableFilter[id].showDialog = true;
            state.visibleFilterMenu = false;
            displayDialogId = id;
        }

        /**
         * フィルタを追加ボタンの位置を取得する
         */
        function getButtonCoordinate() {
            state.buttonCoordinate.x = document.querySelector('#target');
            state.buttonCoordinate.y = document.querySelector('#target');
            // nullではない状況を作る
            if (state.buttonCoordinate.x != null && state.buttonCoordinate.y != null) {
                state.buttonCoordinate.x = state.buttonCoordinate.x.getBoundingClientRect().left - 16;
                state.buttonCoordinate.y = state.buttonCoordinate.y.getBoundingClientRect().bottom + 12;
            }
        }

        return {
            state,
            form,
            isFilterEnabled,
            clear,
            allClear,
            getFiltered,
            getNonFiltered,
            getCondition,
            setFilter,
            setAllFilters,
            closeDialog,
            clickDialogOutside,
            closeFilterMenu,
            inputDate,
            clearDate,
            pulldownClear,
            divisionChange,
            defaultKeyWordPartialMatchValidator,
            defaultOneItemValidator,
            defaultMultiItemValidator,
            defaultDatePeriodValidator,
            defaultNumRangeValidator,
            defaultCheckBoxValidator,
            defaultIssueNumberRadioButtonValidator,
            defaultIssueNumberTextValidator,
            oneSetPullData,
            getOmitString,
            downMenuUpDialog,
            getButtonCoordinate
        };
    }
});
